import { useCallback, useEffect, useRef, useState, type SetStateAction } from 'react'

// Session-only, bounded cache. Tokens isolate sessions; keys include project and
// query parameters. A cached result never skips revalidation on entry.
const cache = new Map<string, unknown>()
const pending = new Map<string, Promise<unknown>>()
const revisions = new Map<string, number>()
function remember<T>(key: string, value: T) {
  cache.delete(key)
  cache.set(key, value)
  if (cache.size > 80) cache.delete(cache.keys().next().value!)
}

export function useSectionResource<T>(token: string, parts: unknown[], fetcher: () => Promise<T>, enabled = true) {
  const key = JSON.stringify([token, ...parts])
  const fetchRef = useRef(fetcher)
  fetchRef.current = fetcher
  const [state, setState] = useState(() => ({ key, data: cache.get(key) as T | undefined, busy: enabled, error: null as unknown }))
  const current = state.key === key ? state : { key, data: cache.get(key) as T | undefined, busy: enabled, error: null }
  const alive = useRef(false)
  const activeKey = useRef(key)
  activeKey.current = key
  const run = useRef(0)

  const refresh = useCallback(async () => {
    if (!enabled) return
    const id = ++run.current
    setState((old) => ({ key, data: old.key === key ? old.data : cache.get(key) as T | undefined, busy: true, error: null }))
    let promise = pending.get(key) as Promise<T> | undefined
    if (!promise) {
      const version = revisions.get(key) ?? 0
      const load = fetchRef.current
      promise = Promise.resolve().then(() => load()).then((data) => {
        // A local edit made after this request started must win over its response.
        if ((revisions.get(key) ?? 0) === version) remember(key, data)
        return cache.get(key) as T
      }).finally(() => { pending.delete(key); if (!pending.has(key)) revisions.delete(key) })
      pending.set(key, promise)
    }
    try {
      const data = await promise
      if (alive.current && activeKey.current === key && run.current === id) setState({ key, data, busy: false, error: null })
    } catch (error) {
      if (alive.current && activeKey.current === key && run.current === id) setState((old) => ({ ...old, busy: false, error }))
    }
  }, [key, enabled])

  useEffect(() => {
    alive.current = true
    void refresh()
    let lastFocus = 0
    const onFocus = () => {
      if (document.visibilityState === 'hidden' || Date.now() - lastFocus < 500) return
      lastFocus = Date.now()
      void refresh()
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onFocus)
    return () => {
      alive.current = false
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onFocus)
    }
  }, [refresh])

  const setData = useCallback((update: SetStateAction<T>) => {
    const previous = cache.get(key) as T
    const next = typeof update === 'function' ? (update as (value: T) => T)(previous) : update
    if (pending.has(key)) revisions.set(key, (revisions.get(key) ?? 0) + 1)
    remember(key, next)
    setState((old) => ({ ...old, key, data: next }))
  }, [key])

  return { data: current.data, setData, loading: enabled && current.data === undefined && !current.error, refreshing: enabled && current.busy, error: current.error, refresh }
}
